import { type ContentsOptions } from 'exa-js';
import {
  type IWebsiteSnapshotPage,
  type IWebsiteSource,
} from './website-snapshot';

/**
 * What one read of a Source produced. When `rootFailed` is true the requested
 * page itself could not be read, so nothing from the read may be used or
 * stored as a Website Snapshot.
 */
export interface ICrawlResult {
  pages: IWebsiteSnapshotPage[];
  /**
   * How many distinct pages worth reading the root page links to on its own
   * site, before at most `WEBSITE_IMPORT_SUBPAGE_COUNT` of them are chosen.
   * It is only logged, so a read cut short by the cap can be told apart from
   * a site with few pages.
   */
  linksFound: number;
  /**
   * How many distinct pages worth reading the search of the practice's own
   * domain found on its site. Like `linksFound` it is only for diagnosis, so
   * a site the search covers poorly can be told apart from one with few pages.
   */
  searchResults: number;
  /**
   * The pages kept plus the chosen pages that failed, so "N of M pages read"
   * never counts a page that was read but left out by the cap on pages kept.
   * A common path the site does not have is left out: it was a guess, not a
   * page that failed to read.
   */
  pagesRequested: number;
  /**
   * How many of the pages chosen from the root page's links and the domain
   * search could not be used. A common path never counts.
   */
  failedPages: number;
  rootFailed: boolean;
}

/**
 * Reads a Source for a Website Import. The import callable depends on this
 * interface rather than on Exa, so it can be tested with a fake reader. A
 * read rejects only before its root page is read; after that, a page it could
 * not read is counted in `failedPages` instead.
 */
export interface IWebsiteCrawler {
  crawl(source: IWebsiteSource): Promise<ICrawlResult>;
}

/**
 * A page as the Exa contents endpoint returns it. `url` is where the page
 * resolved after redirects, which can differ from the address that was
 * requested. This is Exa's wire shape: `title` arrives as JSON null for an
 * untitled page, and `extras` only when the request asked for them.
 */
export interface IExaContentsResult {
  url: string;
  title?: string | null;
  text?: string;
  extras?: IExaContentsExtras;
}

/** Exa's extras for a page; `links` are the links found on it. */
export interface IExaContentsExtras {
  links?: string[];
}

/** Exa's fetch outcome for one requested address; `id` is that address. */
export interface IExaContentsStatus {
  id: string;
  status: string;
}

export interface IExaContentsResponse {
  results: IExaContentsResult[];
  statuses?: IExaContentsStatus[];
}

/**
 * A page the Exa search endpoint found. Only its address is used, and it is
 * optional because this is untrusted wire data: a result without a string
 * address is skipped.
 */
export interface IExaSearchResult {
  url?: string;
}

export interface IExaSearchResponse {
  results: IExaSearchResult[];
}

/**
 * The one search the crawler runs: addresses on the practice's own domain.
 * `contents: false` asks Exa for no page text, as the pages found are read
 * live afterwards anyway.
 */
export interface IExaSearchOptions {
  includeDomains: string[];
  numResults: number;
  contents: false;
}

/**
 * The Exa calls the crawler makes. A real `Exa` client satisfies it, and
 * specs pass a fake instead of constructing a client.
 */
export interface IExaClient {
  getContents(
    urls: string[],
    options: ContentsOptions
  ): Promise<IExaContentsResponse>;
  search(
    query: string,
    options: IExaSearchOptions
  ): Promise<IExaSearchResponse>;
}

/** The pages chosen from the addresses found on a site, best first. */
export interface ISubpageChoice {
  /** At most `WEBSITE_IMPORT_SUBPAGE_COUNT` addresses to read. */
  urls: string[];
  /** How many distinct pages qualified before the cap was applied. */
  linksFound: number;
}

/** An address on the site that may be worth reading, with its ranking. */
export interface IWebsiteSubpageCandidate {
  /** The address resolved against the root page, without its fragment. */
  url: string;
  /** The same for every address of one page, however it is written. */
  pageKey: string;
  /** The number of path segments: shallower pages tend to be broader. */
  depth: number;
  /** The first `WEBSITE_IMPORT_SUBPAGE_TARGETS` term the page matches, or -1. */
  targetTermIndex: number;
  /** Where the address first appears in the list it was chosen from. */
  linkIndex: number;
}

/** The one deadline every Exa request in a crawl races against. */
export interface IWebsiteCrawlDeadline {
  /** Rejects once the crawl has run out of time; it never resolves. */
  passed: Promise<never>;
  /** Whether the crawl has run out of time, so nothing more is requested. */
  hasPassed(): boolean;
  clear(): void;
}
